import { computeTotals, type DocumentFeesConfig, type DocumentLineInput } from '../types/documents.types';
import { isVisualDesignerLine } from './visualDesignerSurveyClone';

export type DepositMode = 'percent' | 'fixed';

export type VersionSnapshotInput = {
  title: string;
  description: string;
  fees: DocumentFeesConfig;
  vatEnabled: boolean;
  vatPct: number;
  inputVatSelected: boolean;
  depositEnabled: boolean;
  depositAmount: number;
  depositMode: DepositMode;
  depositPct: number;
  displayDays: number | null;
  displayHours: number | null;
  paymentStructureApplicable: boolean;
  paymentStructurePattern: string | null;
  lines: DocumentLineInput[];
};

function asNumber(value: unknown, fallback = 0): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function asNumberOrNull(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = asNumber(value, NaN);
  return Number.isFinite(n) ? n : null;
}

function roundMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function syncedDepositAmount(input: {
  enabled: boolean;
  mode: DepositMode;
  pct: number;
  fixed: number;
  grandTotal: number;
}): number {
  if (!input.enabled) return 0;
  if (input.mode === 'percent') return roundMoney(input.grandTotal * input.pct);
  return roundMoney(input.fixed || 0);
}

export function mapApiLinesToEditor(lines: any[] | null | undefined): DocumentLineInput[] {
  return (lines || []).map((line) => ({
    lineKind: line.lineKind ?? line.line_kind,
    additionalType: line.additionalType ?? line.additional_type ?? null,
    materialId: line.materialId ?? line.material_id ?? null,
    equipmentId: line.equipmentId ?? line.equipment_id ?? null,
    labourRateId: line.labourRateId ?? line.labour_rate_id ?? null,
    name: line.name || '',
    description: line.description || null,
    qty: asNumber(line.qty, 0),
    unit: line.unit || null,
    unitPrice: asNumber(line.unitPrice ?? line.unit_price, 0),
    vatInclusive: !!(line.vatInclusive ?? line.vat_inclusive),
    lineTotal: line.lineTotal != null || line.line_total != null
      ? asNumber(line.lineTotal ?? line.line_total, 0)
      : asNumber(line.qty, 0) * asNumber(line.unitPrice ?? line.unit_price, 0),
    sortOrder: asNumberOrNull(line.sortOrder ?? line.sort_order),
    labourDays: asNumberOrNull(line.labourDays ?? line.labour_days),
    labourHours: asNumberOrNull(line.labourHours ?? line.labour_hours),
    metaJson: line.metaJson ?? line.meta_json ?? null,
  }));
}

export function editorLinesFromMapped(
  mapped: DocumentLineInput[],
  stripVisualDesigner: boolean,
): DocumentLineInput[] {
  return stripVisualDesigner ? mapped.filter((line) => !isVisualDesignerLine(line)) : mapped;
}

export function buildVersionSnapshot(input: VersionSnapshotInput) {
  return {
    title: input.title ?? '',
    description: input.description ?? '',
    adminPct: asNumber(input.fees.adminPct),
    adminCap: asNumber(input.fees.adminCap),
    contingencyPct: asNumber(input.fees.contingencyPct),
    profitPct: asNumber(input.fees.profitPct),
    vatEnabled: !!input.vatEnabled,
    vatPct: asNumber(input.vatPct),
    inputVatSelected: !!input.inputVatSelected,
    hasDeposit: !!input.depositEnabled,
    depositAmount: asNumber(input.depositAmount),
    depositPercentage: input.depositMode === 'percent' ? asNumber(input.depositPct) : null,
    projectDays: asNumberOrNull(input.displayDays),
    projectHours: asNumberOrNull(input.displayHours),
    paymentStructureApplicable: !!input.paymentStructureApplicable,
    paymentStructurePattern: input.paymentStructurePattern || null,
    lines: input.lines.map((line) => ({
      lineKind: line.lineKind,
      additionalType: line.additionalType ?? null,
      materialId: line.materialId || null,
      equipmentId: line.equipmentId || null,
      labourRateId: line.labourRateId || null,
      name: line.name,
      description: line.description || null,
      qty: asNumber(line.qty, 0),
      unit: line.unit || null,
      unitPrice: asNumber(line.unitPrice, 0),
      vatInclusive: !!line.vatInclusive,
      lineTotal: line.lineTotal != null ? asNumber(line.lineTotal, 0) : asNumber(line.qty, 0) * asNumber(line.unitPrice, 0),
      sortOrder: asNumber(line.sortOrder, 0),
      labourDays: asNumberOrNull(line.labourDays),
      labourHours: asNumberOrNull(line.labourHours),
      metaJson: line.metaJson ?? null,
    })),
  };
}

export function stringifyVersionSnapshot(input: VersionSnapshotInput): string {
  return JSON.stringify(buildVersionSnapshot(input));
}

export function hydrateDepositAndFees(input: {
  header: any;
  lines: DocumentLineInput[];
  fallbackFees: DocumentFeesConfig;
}): Pick<
  VersionSnapshotInput,
  | 'title'
  | 'description'
  | 'fees'
  | 'vatEnabled'
  | 'vatPct'
  | 'inputVatSelected'
  | 'depositEnabled'
  | 'depositAmount'
  | 'depositMode'
  | 'depositPct'
  | 'displayDays'
  | 'displayHours'
  | 'paymentStructureApplicable'
  | 'paymentStructurePattern'
  | 'lines'
> & { depositFixed: number } {
  const header = input.header || {};
  const nestedFees = header.fees && typeof header.fees === 'object' ? header.fees : {};
  const fees: DocumentFeesConfig = {
    adminPct: asNumber(header.admin_pct ?? header.adminPct ?? nestedFees.adminPct, input.fallbackFees.adminPct),
    adminCap: asNumber(header.admin_cap ?? header.adminCap ?? nestedFees.adminCap, input.fallbackFees.adminCap),
    contingencyPct: asNumber(header.contingency_pct ?? header.contingencyPct ?? nestedFees.contingencyPct, input.fallbackFees.contingencyPct),
    profitPct: asNumber(header.profit_pct ?? header.profitPct ?? nestedFees.profitPct, input.fallbackFees.profitPct),
    developerPct: input.fallbackFees.developerPct ?? null,
  };
  const vatEnabled = !!(header.vat_enabled ?? header.vatEnabled ?? false);
  const vatPct = asNumber(header.vat_pct ?? header.vatPct, 0);
  const depositPercentage = header.deposit_percentage ?? header.depositPercentage;
  const depositMode: DepositMode = depositPercentage != null ? 'percent' : 'fixed';
  const depositPct = asNumber(depositPercentage, 0);
  const depositEnabled = !!(header.has_deposit ?? header.hasDeposit ?? false);
  const depositFixed = depositMode === 'fixed' ? asNumber(header.deposit_amount ?? header.depositAmount, 0) : 0;
  const grandTotal = computeTotals(input.lines, fees, { enabled: vatEnabled, pct: vatPct }).grandTotal;
  return {
    title: header.title ?? '',
    description: header.description ?? '',
    fees,
    vatEnabled,
    vatPct,
    inputVatSelected: !!(header.input_vat_selected ?? header.inputVatSelected ?? false),
    depositEnabled,
    depositAmount: syncedDepositAmount({
      enabled: depositEnabled,
      mode: depositMode,
      pct: depositPct,
      fixed: depositFixed,
      grandTotal,
    }),
    depositMode,
    depositPct,
    depositFixed,
    displayDays: asNumberOrNull(header.project_days ?? header.projectDays),
    displayHours: asNumberOrNull(header.project_hours ?? header.projectHours),
    paymentStructureApplicable: !!(header.payment_structure_applicable ?? header.paymentStructureApplicable),
    paymentStructurePattern: header.payment_structure_pattern ?? header.paymentStructurePattern ?? null,
    lines: input.lines,
  };
}
